import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

export async function loadRaces() {
  const { data, error } = await supabase
    .from('races')
    .select('*')
    .order('date', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function saveRace(race) {
  // Sanitize — ensure arrays are arrays, numbers are numbers
  const clean = {
    id:        String(race.id),
    raceNo:    Number(race.raceNo) || 0,
    date:      String(race.date),
    title:     String(race.title || ""),
    raceClass: String(race.raceClass || "Racing"),
    didNotRace: !!race.didNotRace,
    place:     (race.place !== null && race.place !== undefined && !isNaN(Number(race.place))) ? Number(race.place) : null,
    fleet:     (race.fleet !== null && race.fleet !== undefined && !isNaN(Number(race.fleet))) ? Number(race.fleet) : null,
    time:      String(race.time || ""),
    winner:    String(race.winner || ""),
    twd:       String(race.twd || ""),
    maxSog:    String(race.maxSog || ""),
    avgSog:    String(race.avgSog || ""),
    avgTws:    String(race.avgTws || ""),
    maxTws:    String(race.maxTws || ""),
    distance:  String(race.distance || ""),
    maxPolar:  String(race.maxPolar || ""),
    avgPolar:  String(race.avgPolar || ""),
    // ── Competition / fleet context (times as strings like "0:42:10") ──
    medianTime: String(race.medianTime || ""),
    lastTime:   String(race.lastTime || ""),
    aheadName:  String(race.aheadName || ""),
    aheadTime:  String(race.aheadTime || ""),
    behindName: String(race.behindName || ""),
    behindTime: String(race.behindTime || ""),
    ahead2Name:  String(race.ahead2Name || ""),
    ahead2Time:  String(race.ahead2Time || ""),
    behind2Name: String(race.behind2Name || ""),
    behind2Time: String(race.behind2Time || ""),
    crew:      Array.isArray(race.crew) ? race.crew : [],
    photos:    Array.isArray(race.photos) ? race.photos : [],
    story:     String(race.story || ""),
  };
  const { error } = await supabase
    .from('races')
    .upsert(clean, { onConflict: 'id' });
  if (error) {
    console.error("saveRace error:", error);
    throw new Error(error.message);
  }
}

export async function deleteRace(id) {
  const { error } = await supabase
    .from('races')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ── Crew signups ──────────────────────────────────────────────
export async function loadSignups() {
  const { data, error } = await supabase
    .from('signups')
    .select('*')
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function addSignup(raceDate, name) {
  const { error } = await supabase
    .from('signups')
    .insert({ race_date: raceDate, name: name.trim() });
  if (error) throw error;
}

export async function removeSignup(id) {
  const { error } = await supabase
    .from('signups')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ── Not-racing weeks ──────────────────────────────────────────
export async function loadNotRacing() {
  const { data, error } = await supabase
    .from('not_racing')
    .select('*');
  if (error) throw error;
  return (data || []).map(r => r.race_date);
}

export async function setNotRacing(raceDate, isNotRacing) {
  if (isNotRacing) {
    const { error } = await supabase
      .from('not_racing')
      .upsert({ race_date: raceDate }, { onConflict: 'race_date' });
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('not_racing')
      .delete()
      .eq('race_date', raceDate);
    if (error) throw error;
  }
}

// ── Boat full (skipper-closed weeks) ──────────────────────────
export async function loadBoatFull() {
  const { data, error } = await supabase
    .from('boat_full')
    .select('*');
  if (error) throw error;
  return (data || []).map(r => r.race_date);
}

export async function setBoatFull(raceDate, isFull) {
  if (isFull) {
    const { error } = await supabase
      .from('boat_full')
      .upsert({ race_date: raceDate }, { onConflict: 'race_date' });
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('boat_full')
      .delete()
      .eq('race_date', raceDate);
    if (error) throw error;
  }
}

// ── Reactions ─────────────────────────────────────────────────
export async function loadReactions() {
  const { data, error } = await supabase
    .from('reactions')
    .select('*');
  if (error) throw error;
  return data || [];
}

export async function addReaction(raceDate, emoji, name) {
  const { error } = await supabase
    .from('reactions')
    .insert({ race_date: raceDate, emoji, name });
  if (error) throw error;
}

export async function removeReaction(id) {
  const { error } = await supabase
    .from('reactions')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ── Comments ──────────────────────────────────────────────────
export async function loadComments() {
  const { data, error } = await supabase
    .from('comments')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function addComment(raceDate, name, text) {
  const { data, error } = await supabase
    .from('comments')
    .insert({ race_date: raceDate, name, text })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateComment(id, text) {
  const { error } = await supabase
    .from('comments')
    .update({ text })
    .eq('id', id);
  if (error) throw error;
}

export async function removeComment(id) {
  const { error } = await supabase
    .from('comments')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ── Crew photo append ─────────────────────────────────────────
// Appends photo URLs to a race's existing photos array (crew contribution)
export async function appendRacePhotos(raceId, newUrls) {
  const { data, error: fetchErr } = await supabase
    .from('races').select('photos').eq('id', raceId).single();
  if (fetchErr) throw fetchErr;
  const existing = Array.isArray(data?.photos) ? data.photos : [];
  const merged = [...existing, ...newUrls];
  const { error } = await supabase
    .from('races').update({ photos: merged }).eq('id', raceId);
  if (error) throw error;
  return merged;
}

// ── Skipper announcement ──────────────────────────────────────
// Single-row table 'announcement' (id=1). Holds message + the race_date it pertains to.
export async function loadAnnouncement() {
  const { data, error } = await supabase
    .from('announcement')
    .select('*')
    .eq('id', 1)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function setAnnouncement(message, raceDate) {
  const { error } = await supabase
    .from('announcement')
    .upsert({ id: 1, message, race_date: raceDate, updated_at: new Date().toISOString() });
  if (error) throw error;
}

export async function clearAnnouncement() {
  const { error } = await supabase
    .from('announcement')
    .upsert({ id: 1, message: null, race_date: null, updated_at: new Date().toISOString() });
  if (error) throw error;
}

// ── Boat information ──────────────────────────────────────────
// Single-row table 'boat_info' (id=1). Drives the Boat tab identity card.
export async function loadBoatInfo() {
  const { data, error } = await supabase
    .from('boat_info')
    .select('*')
    .eq('id', 1)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function saveBoatInfo(fields) {
  const { data, error } = await supabase
    .from('boat_info')
    .upsert({ id: 1, ...fields, updated_at: new Date().toISOString() })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Series (groups of races; a race with series_id = null is standalone) ──
export async function loadSeries() {
  const { data, error } = await supabase
    .from('series')
    .select('*')
    .order('sort', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function saveSeries(fields) {
  const { data, error } = await supabase
    .from('series')
    .upsert(fields, { onConflict: 'id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteSeries(id) {
  const { error } = await supabase.from('series').delete().eq('id', id);
  if (error) throw error;
}
